import React from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Block, Heading, Paragraph, Text } from 'suomifi-ui-components'
import { chunk } from 'lodash'
import {
  getUniqueIdentifier,
  isSameHetuOrEidasPID,
} from 'todistukset-common/src/vtj/user.utils'
import LocalizedString from 'todistukset-ui/src/vtj/components/localized-string/LocalizedString.react'
import { UsageRegionTarget } from 'todistukset-common/src/vtj/UsageRegionTarget.enum'
import { EuCountries } from 'todistukset-common/src/vtj/EuCountries.enum'
import { getSubjectPersons } from 'todistukset-common/src/vtj/todistus-person.util'
import {
  CertificatePriceRow,
  CertificatePriceTotal,
  InfoColumn,
  InfoRow,
  PriceRowContainer,
  TextBlock,
  TextNoWrap,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderSummaryStep/OrderSummary.sc'
import TranslationFormWithPreviousAddressesInlineAlert from 'todistukset-ui/src/vtj/components/order-wizard/TranslationFormWithPreviousAddressesInlineAlert.react'
import { UsageRegionReceiver } from 'todistukset-common/src/vtj/UsageRegionReceiver.enum'
import translateLanguage from 'common-translations/src/vtj/translate-language'
import {
  CertificateInformationProps,
  CertificatePriceProps,
  ContactInfoProps,
  DeliveryInfoProps,
  InfoRowProps,
} from 'todistukset-ui/src/vtj/components/order-wizard/OrderSummaryStep/OrderSummary.type'
import { showInformationForCertificateType } from 'todistukset-ui/src/vtj/components/order-wizard/OrderSummaryStep/summary.util'
import CertificateTypes from 'todistukset-common/src/vtj/CertificateTypes.enum'
import { checkIfApostilleIsAvailable } from 'todistukset-ui/src/vtj/apostille.util'
import { getDisplayValueForCountryCode } from 'todistukset-common/src/vtj/localization.util'
import { LanguageCode } from 'common/src/vtj/LanguageCode.enum'
import { TodistusStoreForm } from 'todistukset-common/src/vtj/types/form-types.type'
import { checkIfMailDeliveryIsEnabled } from 'todistukset-common/src/vtj/form-check.util'
import {
  displayNameOf,
  hasName,
  reversedDisplayNameOf,
} from 'todistukset-common/src/vtj/name.util'
import { finalEditableFieldValue } from 'todistukset-common/src/vtj/types/editable-field.type'
import { getIncludeOptionsFromTodistusPyynto } from 'todistukset-common/src/vtj/util/include-options.util'
import type { UsageRegion } from 'todistukset-common/src/vtj/types/todistus'

const getBooleanText = (translate: TFunction, value: boolean | null) => {
  return value
    ? translate('orderWizardOrderSummaryStepSelected')
    : translate('orderWizardOrderSummaryStepNotSelected')
}

const getTargetText = (
  usageRegion: UsageRegion,
  t: TFunction<'todistukset-ui', undefined>,
  lang = LanguageCode.FI
): string => {
  const { target, countryKoodistoCode, receiver } = usageRegion
  if (target === UsageRegionTarget.EU_AUTHORITY) {
    return t('orderWizardUsageRegionStepRegion_eu')
  } else if (
    target === UsageRegionTarget.TARGET_COUNTRY &&
    countryKoodistoCode === EuCountries.FINLAND
  ) {
    return t('orderWizardUsageRegionStepRegion_fi')
  } else {
    // Target is a country, but not finland
    if (!countryKoodistoCode) {
      return ''
    }

    const targetCountryTranslated = getDisplayValueForCountryCode(
      countryKoodistoCode,
      lang
    )

    if (receiver) {
      const receiverStr =
        receiver === UsageRegionReceiver.TARGET_COUNTRY_AUTHORITY
          ? t('orderWizardUsageRegionStepReceiverType_targetCountryAuthority')
          : t('orderWizardUsageRegionStepReceiverType_privateOrOtherOperator')
      return `${targetCountryTranslated}, ${receiverStr}`
    } else {
      return targetCountryTranslated
    }
  }
}

const getVariableFieldsData = (
  t: TFunction,
  todistusForm: TodistusStoreForm,
  certificateType: CertificateTypes
): { labelKey: string; text: string }[][] => {
  const {
    includeHetu,
    includeMaritalStatus,
    includePreviousNames,
    includeSpousePreviousNames,
    includePreviousMarriages,
    includePreviousPermanentAddresses,
    previousAddressesOverYears,
  } = getIncludeOptionsFromTodistusPyynto(todistusForm)

  const variableData = [
    {
      attrName: 'includeHetu',
      labelKey: 'orderWizardOrderSummaryStepHetu',
      text: getBooleanText(t, includeHetu),
    },
    {
      attrName: 'includeMaritalStatus',
      labelKey: 'orderWizardOrderSummaryStepMaritalStatus',
      text: getBooleanText(t, includeMaritalStatus),
    },
    {
      attrName: 'includePreviousNames',
      labelKey: `orderWizardOrderSummaryStepPreviousNames_${certificateType}`,
      text: getBooleanText(t, includePreviousNames),
    },
    {
      attrName: 'includeSpousePreviousNames',
      labelKey: 'orderWizardOrderSummaryStepSpousePreviousNames',
      text: getBooleanText(t, includeSpousePreviousNames),
    },
    {
      attrName: 'includePreviousMarriages',
      labelKey: 'orderWizardOrderSummaryStepSpousePreviousMarriages',
      text: getBooleanText(t, includePreviousMarriages),
    },
    {
      attrName: 'includePreviousPermanentAddresses',
      labelKey: 'orderWizardOrderSummaryStepPreviousAddresses',
      text: `${getBooleanText(t, includePreviousPermanentAddresses)}${
        includePreviousPermanentAddresses
          ? `, ${t('orderWizardOrderSummaryStepPreviousAddressesYears', {
              years: previousAddressesOverYears,
            })}`
          : ''
      }`,
    },
  ].filter(({ attrName }) =>
    showInformationForCertificateType(attrName, certificateType)
  )

  return chunk(variableData, 2)
}

export const DeliveryInfoSection = ({
  todistusForm,
}: DeliveryInfoProps): React.JSX.Element => {
  const { mailDelivery } = todistusForm

  const isMailDelivery = checkIfMailDeliveryIsEnabled(todistusForm)

  return (
    <Block variant={'section'}>
      <Heading variant={'h3'}>
        <LocalizedString
          stringKey={'orderWizardOrderSummaryStepDeliveryMethodTitle'}
        />
      </Heading>

      <Paragraph>
        {!isMailDelivery && (
          <LocalizedString
            stringKey={'orderWizardOrderSummaryStepElectronicDeliveryMethod'}
          />
        )}
        {isMailDelivery && (
          <LocalizedString
            stringKey={'orderWizardOrderSummaryStepPaperDeliveryMethod'}
          />
        )}
      </Paragraph>
      {isMailDelivery && mailDelivery && (
        <InfoRow>
          <InfoColumn>
            <Paragraph>
              <Text variant={'bold'}>
                <LocalizedString
                  stringKey={'orderWizardOrderSummaryStepPaperDeliveryAddress'}
                />
              </Text>
            </Paragraph>
            <TextBlock>
              <Text>{`${finalEditableFieldValue(
                mailDelivery.firstName
              )} ${finalEditableFieldValue(mailDelivery.lastName)}`}</Text>
              <Text>{`${finalEditableFieldValue(
                mailDelivery.streetAddress
              )}`}</Text>
              {finalEditableFieldValue(mailDelivery.streetAddressExtras) && (
                <Text>{`${finalEditableFieldValue(
                  mailDelivery.streetAddressExtras
                )}`}</Text>
              )}
              <Text>{`${finalEditableFieldValue(
                mailDelivery.postalCode
              )} ${finalEditableFieldValue(mailDelivery.postOffice)}`}</Text>
              <Text>{`${finalEditableFieldValue(mailDelivery.country)}`}</Text>
            </TextBlock>
          </InfoColumn>
        </InfoRow>
      )}
    </Block>
  )
}

export const CertificateInformationSection = ({
  todistusForm,
  lang,
  certificateType,
}: CertificateInformationProps): React.JSX.Element => {
  const { t } = useTranslation('todistukset-ui')

  const {
    persons,
    orderer,
    usageRegion,
    todistusLanguage,
    includeApostille,
    includeTranslationForm,
    translationFormLanguage,
  } = todistusForm

  const selectedPersons = getSubjectPersons(persons)

  // In some cases we want to show extra information after the subject's name (such as "orderer")
  const subjectsWithExtraInfo: {
    uniqueIdentifier: string
    name: string
    extraInfo?: string
  }[] = selectedPersons.map((subject) => {
    const name = hasName(subject)
      ? displayNameOf(subject)
      : t('dependantNameNotAvailable')

    const uniqueIdentifier = getUniqueIdentifier(subject)

    if (isSameHetuOrEidasPID(subject, orderer)) {
      return {
        uniqueIdentifier,
        name,
        extraInfo: `(${t('orderWizardOrderSummaryStepClient')})`,
      }
    } else if (subject.isDependant) {
      return {
        uniqueIdentifier,
        name,
        extraInfo: `(${t('orderWizardOrderSummaryStepDependant')})`,
      }
    }
    return { uniqueIdentifier, name }
  })

  const targetText = getTargetText(usageRegion, t, lang)

  const todistusLanguageText = translateLanguage({
    code: finalEditableFieldValue(todistusLanguage),
    lang,
  })

  let translationFormText: string
  if (!includeTranslationForm.ilmoitettuArvo) {
    translationFormText = t('orderWizardOrderSummaryStepNotSelected')
  } else {
    const translationTargetLanguageStr = translateLanguage({
      code: finalEditableFieldValue(translationFormLanguage),
      lang,
    })

    translationFormText = t('orderWizardOrderSummaryStepTranslationForm', {
      source: todistusLanguageText,
      target: translationTargetLanguageStr,
    })
  }

  const variableDataRows = getVariableFieldsData(
    t,
    todistusForm,
    certificateType
  )

  const translationRow = checkIfApostilleIsAvailable({ usageRegion })
    ? [
        {
          labelKey: 'orderWizardOrderSummaryStepApostille',
          text: getBooleanText(t, includeApostille.ilmoitettuArvo),
        },
      ]
    : [
        {
          labelKey: 'orderWizardOrderSummaryStepEuTranslationForm',
          text: translationFormText,
        },
      ]

  return (
    <Block variant={'section'}>
      <Heading variant={'h3'}>
        <LocalizedString
          stringKey={'orderWizardOrderSummaryStepCertificateInformationTitle'}
        />
      </Heading>
      <Paragraph>
        <LocalizedString
          stringKey={
            'orderWizardOrderSummaryStepCertificateInformationDescription'
          }
        />
      </Paragraph>
      <InfoRow>
        <InfoColumn variant={'div'} key={'label'} width="100%">
          <Paragraph>
            <Text variant={'bold'}>
              <LocalizedString
                stringKey={'orderWizardOrderSummaryStepPersons'}
              />
            </Text>
          </Paragraph>
          <TextBlock>
            {subjectsWithExtraInfo.map((s) => {
              if (s.extraInfo) {
                return (
                  <Block key={s.uniqueIdentifier}>
                    <Text>{s.name} </Text>
                    <Text color="depthDark2" smallScreen>
                      {s.extraInfo}
                    </Text>
                  </Block>
                )
              }
              return <Text key={s.uniqueIdentifier}>{s.name} </Text>
            })}
          </TextBlock>
        </InfoColumn>
      </InfoRow>

      {variableDataRows.map((pair) => (
        <CertificateInfoRow key={pair[0].labelKey} data={pair} />
      ))}

      <CertificateInfoRow
        data={[
          {
            labelKey: 'orderWizardOrderSummaryStepPurpose',
            text: targetText,
          },
          {
            labelKey: 'orderWizardOrderSummaryStepCertificateLanguage',
            text: todistusLanguageText,
          },
        ]}
      />
      <CertificateInfoRow data={translationRow} />
      <TranslationFormWithPreviousAddressesInlineAlert
        certificateType={certificateType}
      />
    </Block>
  )
}

export const CertificateInfoRow = ({
  data,
}: InfoRowProps): React.JSX.Element => {
  return (
    <InfoRow>
      {data.map(({ labelKey, text }) => (
        <InfoColumn
          variant={'div'}
          key={labelKey}
          width={`${100 / data.length}%`}
        >
          <Paragraph>
            <Text variant={'bold'}>
              <LocalizedString stringKey={labelKey} />
            </Text>
          </Paragraph>
          <Paragraph>
            <Text>{text}</Text>
          </Paragraph>
        </InfoColumn>
      ))}
    </InfoRow>
  )
}

export const CertificatePrice = ({
  todistusPrice,
  totalPrice,
  todistusProduct,
  todistusForm,
}: CertificatePriceProps): React.JSX.Element => {
  const { t } = useTranslation('todistukset-ui')
  const { persons } = todistusForm
  const selectedPersons = getSubjectPersons(persons)
  return (
    <Block variant={'section'}>
      <Heading variant={'h3'}>
        <LocalizedString
          stringKey={'orderWizardOrderSummaryStepCertificatePriceTitle'}
        />
      </Heading>
      <PriceRowContainer>
        {selectedPersons.map((subject) => {
          const subjectName = hasName(subject)
            ? reversedDisplayNameOf(subject)
            : t('dependantNameNotAvailable')
          return (
            <CertificatePriceRow key={getUniqueIdentifier(subject)}>
              <Text>
                {`${t(
                  `productTitleFor_${todistusProduct.productType}`
                )} / ${subjectName}`}
              </Text>
              <TextNoWrap>{todistusPrice}&nbsp;€</TextNoWrap>
            </CertificatePriceRow>
          )
        })}
      </PriceRowContainer>
      <CertificatePriceTotal>
        <CertificatePriceRow>
          <Text variant={'bold'}>
            <LocalizedString
              stringKey={'orderWizardOrderSummaryStepCertificatePriceTotal'}
            />
          </Text>
          <Text variant={'bold'}>{totalPrice}&nbsp;€</Text>
        </CertificatePriceRow>
      </CertificatePriceTotal>
    </Block>
  )
}

export const ContactInfoSection = ({
  todistusForm,
}: ContactInfoProps): React.JSX.Element => {
  const {
    contactInfo: { email, phoneNumber },
  } = todistusForm
  return (
    <Block variant={'section'}>
      <Heading variant={'h3'}>
        <LocalizedString
          stringKey={'orderWizardOrderSummaryStepContactInfoTitle'}
        />
      </Heading>
      <CertificateInfoRow
        data={[
          {
            labelKey: 'orderWizardOrderSummaryStepContactInfoEmail',
            text: finalEditableFieldValue(email) || '-',
          },
        ]}
      />
      <CertificateInfoRow
        data={[
          {
            labelKey: 'orderWizardOrderSummaryStepContactInfoPhone',
            text: finalEditableFieldValue(phoneNumber) || '-',
          },
        ]}
      />
    </Block>
  )
}
